// Credit packs (PLAN-offers.md §7.5): prepaid usage on sale. Not versioned: a pending order keeps the price it
// was made with, a grant its amount, so editing a pack in place is safe. A pack is in one currency, its price and
// credit too; the same key in another currency is another pack (PLAN-multi-currency.md).
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { errorMessage, useAsync } from '../../hooks/useAsync';
import { keyIn, money, toDecimal } from '../../services/format';
import { useRatedCurrencyCodes } from '../../hooks/useCurrencies';
import { Alert, Badge, Button, Card, Cell, Checkbox, Field, Input, PageTitle, Row, Select, Table } from '../../components/ui';
import type { CreditPackAdmin } from '../../types/api';

interface Draft {
  key: string;
  name: string;
  currency: string;
  price: string;
  credit: string;
  validity: string;
  sort: string;
  public: boolean;
  active: boolean;
}

const EMPTY: Draft = { key: '', name: '', currency: 'EUR', price: '', credit: '', validity: '12', sort: '0', public: true, active: true };

function draftOf(p: CreditPackAdmin): Draft {
  return { key: p.key, name: p.name, currency: p.currency, price: p.price, credit: p.credit_eur, validity: p.validity_months === null ? '' : String(p.validity_months), sort: String(p.sort), public: p.public, active: p.active };
}

export function PacksList() {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const { data, error, loading, reload } = useAsync(() => api.packs(), []);
  const currencies = useRatedCurrencyCodes();
  const [editing, setEditing] = useState<CreditPackAdmin | 'new' | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [fail, setFail] = useState<string | null>(null);
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));

  const edit = (p: CreditPackAdmin | 'new') => {
    setEditing(p);
    setDraft(p === 'new' ? EMPTY : draftOf(p));
    setFail(null);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const price = toDecimal(draft.price);
    const credit = toDecimal(draft.credit);
    if (!price || !credit) return;
    const common = { name: draft.name.trim(), price, credit_eur: credit, validity_months: draft.validity.trim() ? parseInt(draft.validity, 10) || null : null, public: draft.public, active: draft.active, sort: parseInt(draft.sort, 10) || 0 };
    setBusy(true);
    setFail(null);
    try {
      if (editing === 'new') await api.createPack({ ...common, key: draft.key.trim().toLowerCase(), currency: draft.currency });
      else if (editing) await api.patchPack(editing.id, common);
      setEditing(null);
      reload();
    } catch (err) {
      setFail(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageTitle title={t('packs.title')} subtitle={t('packs.subtitle')} actions={<Button variant="primary" onClick={() => edit('new')}>{t('packs.new')}</Button>} />
      {error && <Alert>{error}</Alert>}
      <div className="grid gap-4 xl:grid-cols-[1fr_360px]">
        <Table head={[t('packs.name'), t('packs.price'), t('packs.credit'), t('packs.validity'), t('offers.visibility'), '']} empty={loading ? t('common.loading') : t('common.empty')}>
          {(data ?? []).map((p) => (
            <Row key={p.id}>
              <Cell>
                {p.name} <span className="font-mono text-[10.5px] text-faint">{keyIn(p.key, p.currency)}</span>
              </Cell>
              <Cell mono>{money(p.price, p.currency, l)}</Cell>
              <Cell mono>{money(p.credit_eur, p.currency, l)}</Cell>
              <Cell mono>{p.validity_months === null ? t('packs.noExpiry') : t('packs.months', { count: p.validity_months })}</Cell>
              <Cell>{!p.active ? <Badge tone="bad">{t('offers.inactive')}</Badge> : p.public ? <Badge tone="good">{t('offers.public')}</Badge> : <Badge>{t('offers.private')}</Badge>}</Cell>
              <Cell>
                <Button variant="ghost" onClick={() => edit(p)} data-testid={`edit-${p.key}-${p.currency}`}>
                  {t('common.edit')}
                </Button>
              </Cell>
            </Row>
          ))}
        </Table>
        {editing && (
          <form onSubmit={submit}>
            <Card title={editing === 'new' ? t('packs.new') : `${editing.name} · ${editing.currency}`}>
              <div className="flex flex-col gap-3">
                {editing === 'new' && (
                  <div className="grid grid-cols-[1fr_96px] gap-3">
                    <Field label={t('offers.key')} hint={t('offers.keyHint')}>
                      <Input required pattern="[a-z0-9][a-z0-9-]*" value={draft.key} onChange={(e) => set({ key: e.target.value })} data-testid="pack-key" />
                    </Field>
                    <Field label={t('offers.currency')}>
                      <Select value={draft.currency} onChange={(e) => set({ currency: e.target.value })} data-testid="pack-currency">
                        {currencies.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  </div>
                )}
                <Field label={t('packs.name')}>
                  <Input required value={draft.name} onChange={(e) => set({ name: e.target.value })} data-testid="pack-name" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label={t('packs.price')} hint={t('packs.priceHint')}>
                    <Input required type="number" min={0} step="0.01" value={draft.price} onChange={(e) => set({ price: e.target.value })} data-testid="pack-price" />
                  </Field>
                  <Field label={t('packs.credit')} hint={t('packs.creditHint')}>
                    <Input required type="number" min={0.01} step="0.01" value={draft.credit} onChange={(e) => set({ credit: e.target.value })} data-testid="pack-credit" />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label={t('packs.validity')} hint={t('packs.validityHint')}>
                    <Input type="number" min={1} value={draft.validity} onChange={(e) => set({ validity: e.target.value })} />
                  </Field>
                  <Field label={t('offers.sort')}>
                    <Input type="number" value={draft.sort} onChange={(e) => set({ sort: e.target.value })} />
                  </Field>
                </div>
                <Checkbox label={t('packs.publicHint')} checked={draft.public} onChange={(e) => set({ public: e.target.checked })} />
                <Checkbox label={t('packs.activeHint')} checked={draft.active} onChange={(e) => set({ active: e.target.checked })} />
                {editing !== 'new' && <p className="text-[11.5px] text-faint">{t('packs.editHint')}</p>}
                {fail && <Alert>{fail}</Alert>}
                <div className="flex justify-end gap-2">
                  <Button onClick={() => setEditing(null)}>{t('common.cancel')}</Button>
                  <Button type="submit" variant="primary" busy={busy} data-testid="pack-save">
                    {editing === 'new' ? t('common.create') : t('common.save')}
                  </Button>
                </div>
              </div>
            </Card>
          </form>
        )}
      </div>
    </div>
  );
}
